<template>
  <div>
    <quill-editor
        ref="quillRef"
        class="editor"
        :class="{
        'community-chat' : chat,
        'community-chat-safari' : this.isSafari
      }"
        :options="editorOption"
        v-model="content"
        @change="onChange()"
    >
    </quill-editor>
    <div v-if="autoSave" class="autosaveText fade-in-text">
      <i class="fas fa-check-circle" /> Autosave Completed!
    </div>
    <div v-if="maxLength"
         class="char-count char-count-return"
         :class="{
           exceeded: charactersRemaining < 0,
           'remove-overlapping-borders': chat
         }">
      {{charactersRemaining}} {{$t('messaging.charactersLeft')}}
      <div v-if="chat"
           class="return-message is-size-7 has-text-grey-light">
        <strong class="has-text-weight-semibold has-text-grey-light">
          {{$t('messaging.return')}} </strong> &nbsp;<span v-if="!isMobile()">|&nbsp; <strong
          class="has-text-weight-semibold has-text-grey-light">{{$t('messaging.shift')}}</strong> {{
          $t('messaging.add')
        }}</span>
      </div>
    </div>
    <div v-else-if="maxWords && contentLength"
         class="char-count"
         :class="{exceeded: wordsRemaining < 0}">
      {{wordsRemaining}} {{$t('messaging.wordsLeft')}}
    </div>
  </div>
</template>

<script>
import FilterWords from 'bad-words'
import katex from 'katex'
import 'katex/dist/katex.min.css'
import marked from '@/libs/marked'
import 'highlight.js/styles/monokai-sublime.css'
import 'quill/dist/quill.core.css'
import 'quill/dist/quill.snow.css'
import 'quill-emoji/dist/quill-emoji.css'
import breakpoints from '@/libs/breakpoints'
import hljs from 'highlight.js'
import { Quill, quillEditor } from 'vue-quill-editor'
import quillEmoji from 'quill-emoji'
import quillMention from 'quill-mention'
import truncate from 'html-truncate'
import userAgent from '@/libs/user-agent'
import auth from '@/mixins/auth'
import i18n from '@/i18n'
import profanity from '@/locales/profanity.json'
import save from '@/components/Forms/Create/mixins/save'

const link = Quill.import('formats/link')
const icons = Quill.import('ui/icons')

const win = window
win.katex = katex

icons.bold = '<i class="fal fa-bold"></i>'
icons.italic = '<i class="fal fa-italic" aria-hidden="true"></i>'
icons.header = '<i class="fal fa-heading" aria-hidden="true"></i>'
icons.list.ordered = '<i class="fal fa-list-ol" aria-hidden="true"></i>'
icons.list.bullet = '<i class="fal fa-list-ul" aria-hidden="true"></i>'
icons.blockquote = '<i class="fal fa-quote-left" aria-hidden="true"></i>'
icons.link = '<i class="fal fa-link" aria-hidden="true"></i>'
icons.script.sub = '<i class="fal fa-subscript" aria-hidden="true"></i>'
icons.script.super = '<i class="fal fa-superscript" aria-hidden="true"></i>'
icons.clean = '<i class="fal fa-eraser" aria-hidden="true"></i>'
icons['code-block'] = '<i class="fal fa-code" aria-hidden="true"></i>'
icons.formula = '<i class="fal fa-function" aria-hidden="true"></i>'
icons.indent['-1'] = '<i class="fal fa-outdent" aria-hidden="true"></i>'
icons.indent['+1'] = '<i class="fal fa-indent" aria-hidden="true"></i>'

export default {
  components: {
    quillEditor,
    quillEmoji,
    quillMention,
    FilterWords
  },
  props: ['value', 'maxLength', 'maxWords', 'placeholder', 'chat', 'isPost'],
  mixins: [auth, save],
  data: function () {
    return {
      autoSave: false,
      timeOut: null,
      loadedLength: 0,
      editorOption: {
        theme: 'snow',
        bounds: '#post-body',
        modules: {
          'emoji-toolbar': true,
          'emoji-shortname': false,
          formula: true,
          syntax: {
            highlight: text => hljs && hljs.highlightAuto(text).value
          },
          toolbar: [
            ['bold'],
            ['italic'],
            [{ header: 2 }],
            [{ color: [] }, { background: [] }],
            [{ align: ['', 'center', 'right', 'justify'] }],
            [{ list: 'ordered' }],
            [{ list: 'bullet' }],
            ['blockquote'],
            ['code-block'],
            ['formula'],
            ['link'],
            [{ script: 'sub' }, { script: 'super' }],
            ['emoji'],
            ['clean']
          ],
          mention: {
            allowedChars: /^[A-Za-z0-9_.,\sÅÄÖåäö]*$/,
            mentionDenotationChars: ['@'],
            defaultMenuOrientation: 'top',
            isolateCharacter: true,
            maxChars: 31,
            source: async (searchTerm, renderList) => {
              if (searchTerm.length === 0) {
                return renderList(null)
              }

              const sanitizedSearchTerm = searchTerm.toLowerCase().trim().replace(',', '')
              const matchedUsers = await this.suggestPeople(sanitizedSearchTerm)
              const users = this.setMatchedUsers(matchedUsers, sanitizedSearchTerm)

              renderList(users)

              const hasSpaces = this.checkForSpaces(searchTerm)

              if (hasSpaces) {
                if (this.hasExactMatch) {
                  this.insertMention()
                  this.hasExactMatch = !this.hasExactMatch
                  return
                }
                return renderList(null)
              }
              this.hasExactMatch = false
            },
            renderItem: (user) => {
              return `<div class='img-container'><img src='${user.avatar}'></div><p>${user.userName}</p>`
            },
            renderLoading: () => {
              return '<div class=\'loader is-size-5\'></div>'
            },
            // passing data between quill component and mentions object
            suggestPeople: this.suggestPeople,
            setMatchedUsers: this.setMatchedUsers,
            checkForSpaces: this.checkForSpaces,
            insertMention: this.insertMention,
            hasExactMatch: this.hasExactMatch
          }
        },
        placeholder: this.placeholder
      },
      content: '',
      valueText: '',
      quillContents: '',
      quillText: '',
      isChanging: false,
      hasExactMatch: false,
      bold: i18n.t('quilltips.bold'),
      italic: i18n.t('quilltips.italic'),
      header: i18n.t('quilltips.header'),
      numbered: i18n.t('quilltips.numbered'),
      bulleted: i18n.t('quilltips.bulleted'),
      quote: i18n.t('quilltips.quote'),
      clear: i18n.t('quilltips.clear')
    }
  },
  mounted: function () {
    hljs.highlightAll()
    this.setEncodedContents(this.value)
    this.addLinkHandler()
    this.autoLinkGen()

    const qlBold = document.querySelector('.ql-bold')
    if (qlBold) { qlBold.setAttribute('data-balloon', this.bold + ' (Ctrl + b)') }
    const qlItalic = document.querySelector('.ql-italic')
    if (qlItalic) { qlItalic.setAttribute('data-balloon', this.italic + ' (Ctrl + i)') }
    const qlHeader = document.querySelector('.ql-header')
    if (qlHeader) { qlHeader.setAttribute('data-balloon', this.header) }
    const btnOrdered = document.querySelector('button[value=\'ordered\']')
    if (btnOrdered) { btnOrdered.setAttribute('data-balloon', this.numbered) }
    const btnBullet = document.querySelector('button[value=\'bullet\']')
    if (btnBullet) { btnBullet.setAttribute('data-balloon', this.bulleted) }
    const qlBlockquote = document.querySelector('.ql-blockquote')
    if (qlBlockquote) { qlBlockquote.setAttribute('data-balloon', this.quote) }
    const qlLink = document.querySelector('.ql-link')
    if (qlLink) { qlLink.setAttribute('data-balloon', 'Link') }
    const btnSub = document.querySelector('button[value=\'sub\']')
    if (btnSub) { btnSub.setAttribute('data-balloon', 'Subscript') }
    const btnSuper = document.querySelector('button[value=\'super\']')
    if (btnSuper) { btnSuper.setAttribute('data-balloon', 'Superscript') }
    // const qlEmoji = document.querySelector(`.ql-emoji`)
    // if (qlEmoji) { qlEmoji.setAttribute(`data-balloon`, `Emoji`) }
    const qlClean = document.querySelector('.ql-clean')
    if (qlClean) { qlClean.setAttribute('data-balloon', this.clear) }
    this.loadedLength = this.content.length
  },
  computed: {
    encodedContents: function () {
      return 'quill' + JSON.stringify(this.quillContents)
    },
    quillRef: function () {
      return this.$refs.quillRef.quill
    },
    contentLength: function () {
      return this.quillText ? this.quillText.trim().length : 0
    },
    charactersRemaining: function () {
      return this.maxLength - this.contentLength
    },
    wordCount: function () {
      if (this.quillText) {
        const matches = this.quillText.trim().match(/\b[-?(\w+)?A-zÀ-ÿ'àâäèéêëîïôœùûüÿçÀÂÄÈÉÊËÎÏÔŒÙÛÜŸÇ]+\b/gi)
        if (matches) {
          return matches.length
        }
      }
      return 0
    },
    wordsRemaining: function () {
      return this.maxWords - this.wordCount
    },
    isSafari: function () {
      return userAgent.isSafari()
    },
    hasNonCharContent: function () {
      if (this.quillContents) {
        const body = this.quillContents?.ops[0]?.insert
        const hasMention = body?.mention && body.mention.value
        const hasEmoji = body?.emoji
        const hasFormula = body?.formula

        if (body && (hasMention || hasEmoji || hasFormula)) {
          return true
        }
      }
      return false
    }
  },
  methods: {
    setText: function (text) {
      this.content = text
    },
    setContents: function (contents) {
      this.quillRef.setContents(contents)
    },
    setEncodedContents: function (contents) {
      if (contents.indexOf('quill') === 0) {
        this.setContents(JSON.parse(contents.substring('quill'.length)))
      } else {
        this.content = marked(contents)
      }
    },
    onChange: function () {
      this.quillContents = this.quillRef.getContents()
      this.quillText = this.quillRef.getText()
      this.$emit('input', this.encodedContents)
      // Auto save idea
      const self = this
      // Check for post before updating
      if (this.loadedLength > 0 && this.isPost) {
        clearTimeout(this.timeOut)
        this.timeOut = setTimeout(async () => {
          await this.save()
          this.autoSave = true
          setTimeout(() => {
            this.autoSave = false
          }, 5000)
        }, 3000)
      } else {
        this.loadedLength = self.content.length
      }
    },
    focusAtEnd: function () {
      this.quillRef.focus()
      const value = this.contentLength
      this.quillRef.setSelection(value)
    },
    getHTML: function (content, maxLength) {
      let html = ''
      if (content.indexOf('quill') !== 0) {
        html = marked(content)
      } else {
        this.setContents(JSON.parse(content.substring('quill'.length)))
        html = this.content
      }
      if (maxLength) {
        const hasMention = /<span class="mention"(.*?)>@<\/span>/g
        html = html.replace(hasMention, '@')
        html = truncate(html, maxLength)
      }
      return html
    },
    isMobile: function () {
      return breakpoints.isMobile()
    },
    suggestPeople: async function (searchName) {
      return this.$store.dispatch('getAllLikeName', { query: searchName, limit: 10 }).then((response) => {
        return response.users
      })
    },
    setMatchedUsers: function (matchedUsers, searchTerm) {
      this.assignValues(matchedUsers)
      this.sortMatchToTop(matchedUsers, searchTerm)
      return matchedUsers
    },
    assignValues: function (matchedUsers) {
      return matchedUsers.forEach(user => {
        user.value = user.userName
        user.link = `${this.getAppURL()}/profile/${user.userName}`
      })
    },
    sortMatchToTop: function (matchedUsers, searchTerm) {
      const first = searchTerm
      const hasOnlyOneMatch = matchedUsers.length === 1
      const isMatching = matchedUsers[0]?.userName.toLowerCase() === first

      if (hasOnlyOneMatch && isMatching) {
        this.hasExactMatch = true
      } else {
        matchedUsers.sort((x, y) => {
          if (x.userName.toLowerCase() === first || y.userName.toLowerCase() === first) this.hasExactMatch = true
          return x.userName.toLowerCase() === first ? -1 : y.userName.toLowerCase() === first ? 1 : 0
        })
      }
    },
    checkForSpaces: function (searchTerm) {
      const array = searchTerm.split(' ')
      return array.length >= 2
    },
    getAppURL: function () {
      return process.env.WEB_ROOT
    },
    insertMention: function () {
      const event = { code: 'Enter', key: 'Enter', charKode: 13, keyCode: 13, view: window }
      const keyboardEvent = new KeyboardEvent('keydown', event)
      const quillEditor = this.$refs.quillRef.quill.root

      this.$nextTick(() => {
        quillEditor.dispatchEvent(keyboardEvent)
      })
    },
    getUserToReply: function (user) {
      const userData = {}
      const link = `"${this.getAppURL()}/profile/${user.userName}"`

      userData.value = `<a href=${link} target=_blank>${user.userName}`
      userData.denotationChar = '@'
      userData.id = user.id
      userData.index = 0
      userData.link = link

      this.quillRef.getModule('mention').insertItem(userData, true)
    },
    addLinkHandler: function () {
      link.sanitize = value => {
        if (value.substring(0, 4) !== 'http' && value.substring(0, 1) !== '/') {
          value = `http://${value}`
        }
        return value
      }
    },
    hasProfanity: function () {
      const body = this.quillContents?.ops[0]?.insert
      const filter = new FilterWords()

      filter.removeWords(...profanity.whiteList)
      filter.addWords(...profanity.blackList)

      const comment = body.split(' ')
      for (let i = 0; i < comment.length; i++) {
        if (filter.isProfane(comment[i])) {
          return `This word can be taken offensively on our platform, please remove "${comment[i]}" before being able to save`
        }
      }
    },
    sliceFromLastWhitespace: function (str) {
      const whitespaceI = str.lastIndexOf(' ')
      const sliceI = whitespaceI === -1 ? 0 : whitespaceI + 1
      return str.slice(sliceI)
    },
    autoLinkGen: function () {
      const REGEXP = /(https?:\/\/|www\.)[\S]+/
      this.quillRef.keyboard.addBinding({
        collapsed: true,
        key: ' ',
        prefix: REGEXP,
        handler: (range, context) => {
          const url = this.sliceFromLastWhitespace(context.prefix)
          const retain = range.index - url.length
          const ops = retain ? [{ retain }] : []
          ops.push(
            { delete: url.length },
            { insert: url, attributes: { link: url } }
          )
          this.quillRef.updateContents({ ops })
          return true
        }
      })
    }
  }
}
</script>

<style lang="scss">
@import '~styles/utils.scss';
$mention-list-item-height: 36.8px;
.ql-snow.ql-toolbar button svg, .ql-snow .ql-toolbar button svg {
  width: 0px !important;
}
button.ql-emoji:after {
  content: '\f118';
  font-family: 'FontAwesome';
}

.ql-editor {
  min-height: 90px;
  font-size: 1rem;
  font-family: inherit;
  -webkit-user-select: text;
}

.ql-header,
.ql-size {
  .ql-picker-label:after {
    color: $black !important;
    content: '\f078';
    font-family: 'FontAwesome';
    position: absolute;
    top: 1px !important;
    right: 0;
  }
}
.ql-snow.ql-toolbar button, .ql-snow .ql-toolbar button {
  padding: 6px 11px;
  color: $grey;
  font-size: 0.9rem;
  width: auto;
  height: auto;
  &:hover {
    background-color: $grey-lighter;
  }
  @include mobile {
    padding: 6px 9px;
  }
}
.ql-formats.button:last-of-type {
  &:hover {
    .fal {
      color: yellow;
      font-weight: 600;
      text-shadow: -1.3px 0 $grey-darker, 0 1.3px $grey-darker, 1.3px 0 $grey-darker, 0 -1.3px $grey-darker;
    }
  }
  @include mobile {
    font-weight: 400;
    color: $grey;
  }
}
.pell-button-selected {
  background-color: $grey-darker;
}
.ql-formats .fa-smile, .ql-formats .fa-eraser {
  font-size: 1.03rem;
}
.emoji_completions {
  z-index: 10;
}

.ql-toolbar .ql-formats:nth-of-type(10), .ql-toolbar .ql-formats:nth-of-type(8), .ql-toolbar .ql-formats:nth-of-type(11), .ql-toolbar .ql-formats:nth-of-type(12)  {
  @include mobile {
    display: none;
  }
}

.mention a {
  color: #00ADEE;
}
.ql-mention-denotation-char {
  color: #00ADEE;
  text-decoration: underline;
}

.ql-mention-list-container {
  padding: 0.5rem;
  background: white;
  border-radius: 4px;
  box-shadow: 0 .5em 1em -.125em rgba(10,10,10,.1),0 0 0 1px rgba(10,10,10,.02);
  z-index: 3;
  max-height: calc((#{$mention-list-item-height} * 5) + 1rem);
  overflow-y: auto;
  .ql-mention-list-item {
    display: flex;
    align-items: center;
    padding: 0.4rem 0.3rem;
    font-size: 1rem;
    cursor: pointer;
    &:hover,
    &.selected {
      background: darken(white, 5%);
    }
    .img-container {
      max-width: 1.5rem;
      max-height: 1.5rem;
      border-radius: 50%;
      overflow: hidden;
      margin-right: 10px;
      img {
        display: block;
      }
    }
  }
}

// COMMUNITY CHAT QUILL STYLES
.community-chat.quill-editor {
  padding-bottom: 0;
  margin-bottom: 0;
  display: flex;
  flex-direction: column;
  justify-content: center;
  .ql-toolbar,
  .ql-container {
    border-left: none;
    border-right: none;
  }
  .ql-container.ql-snow {
    height: auto;
    flex-grow: 1;
  }
  .ql-container {
    #emoji-palette {
      top: -130px !important;
    }
  }
  .ql-editor {
    max-height: 15rem;
    overflow-y: scroll;
  }
  @include mobile {
    .ql-toolbar,
    .ql-container {
      border-bottom: none;
      border-top: 1px solid #ccc;
    }
    .ql-container.ql-snow {
      flex-grow: 0;
      border-radius: 25px;
      border: 1px solid #ccc;
      margin: 0 0.3rem;
      background: white;
    }
    .ql-editor {
      max-height: 8rem;
      min-height: auto;
    }
    @include fix-ios-scrolling;
  }
}

</style>

<style lang="scss" scoped>
@import '~styles/utils.scss';
.char-count {
  border: 1px solid #ccc;
  border-width: 0px 1px 1px 1px;
  color: #aaa;
  padding: 5px 15px;
  text-align: right;
  font-size: 0.8rem;
  &.exceeded {
    color: $danger;
  }
  &.remove-overlapping-borders {
    border-left: none;
    border-right: none;
  }
}

.char-count-return {
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  align-items: flex-start;
  @include fix-ios-scrolling;
}

.autosaveText {
  float: right;
  margin: -20px 5px 0 0;
  font-weight: 600;
  font-size: .7em;
  color: $primary;
  @include mobile-and-ipad {
    font-size: 1em;
    margin: -25px 5px 0 0;
  }
}

.fade-in-text {
  animation: fadeIn 5s;
  -webkit-animation: fadeIn 5s;
  -moz-animation: fadeIn 5s;
  -o-animation: fadeIn 5s;
  -ms-animation: fadeIn 5s;
}
@keyframes fadeIn {
  0% {opacity:0;}
  100% {opacity:1;}
}

@-moz-keyframes fadeIn {
  0% {opacity:0;}
  100% {opacity:1;}
}

@-webkit-keyframes fadeIn {
  0% {opacity:0;}
  100% {opacity:1;}
}

@-o-keyframes fadeIn {
  0% {opacity:0;}
  100% {opacity:1;}
}

@-ms-keyframes fadeIn {
  0% {opacity:0;}
  100% {opacity:1;}
}
</style>
